import { Injectable } from '@angular/core';
import { AppService } from 'app/app.service';
import { Subject, Observable } from 'rxjs';
import { MapCoordinates, MapMarker } from './map.component';
import { SearchResult } from '../search-results-list/search-results-list.component';

export type MapToolsLoading = 'coordinates' | 'search';

@Injectable({
    providedIn: 'root',
})
export class MapToolsService {
    private promisesCache: { [key: string]: Promise<any> } = {};

    loadingSubject = new Subject<MapToolsLoading>();
    loading$: Observable<MapToolsLoading> = this.loadingSubject.asObservable();

    constructor(public app: AppService) {
    }

    async getCoordinatesDetails(lat: number, lon: number): Promise<MapCoordinates> {
        this.loadingSubject.next('coordinates');

        try {
            const id = `map.geocodeReverse/${this.app.client.id}/${lat}/${lon}`;
            const cache = await this.promisesCache[id];
            const response = cache || await this.app.api.map.geocodeReverse(this.app.client.id, lat, lon).then(x => (x.items || [])[0]);

            this.promisesCache[id] = response;

            return response ? {
                address: response.text,
                lat: response.lat,
                lon: response.lon,
                speed: response.speed,
            } : {};
        } catch (error) {
            return { error };
        } finally {
            this.loadingSubject.next(null);
        }
    }

    async doSearch(term: string, action: (marker: MapMarker) => void): Promise<SearchResult<MapCoordinates>[]> {
        if (!term) { return; }

        this.loadingSubject.next('search');

        try {
            const icons = {
                location: 'location-arrow',
                keepin: 'draw-square',
                nogo: 'do-not-enter',
                route: 'route',
                address: 'map-marker-alt',
            };

            const id = `map.geocodeForward/${this.app.client.id}/${term}`;
            const cache = await this.promisesCache[id];
            const response = cache || await this.app.api.map.geocodeForward(this.app.client.id, term).then(x => x.items || []);

            this.promisesCache[id] = response;

            return response.map(x => ({
                id: x.id,
                value: x.text,
                meta: { address: x.text, lon: x.lon, lat: x.lat },
                icon: icons[x.type],
                action: () => {
                    const marker = {
                        id: x.id,
                        name: x.text,
                        lon: x.lon,
                        lat: x.lat,
                        bounds: x.bounds,
                        getIcon: () => ({ size: 0, html: `<i class="location-icon map-font-icon__shadows icon icon-${icons[x.type]}"></i>` }),
                    };
                    action(marker);
                },
            }));
        } catch (error) {
            console.error(error);
            // empty list will show message of no items found which is okay
            return [];
        } finally {
            this.loadingSubject.next(null);
        }
    }
}
